/**
 * Mesures géométriques des entités DR — longueur, surface et orientation, en
 * UNITÉS DU PLAN (les plans d'ouvrage sont généralement dessinés en mètres ;
 * la calibration d'unité par dessin est un palier ultérieur).
 *
 * Tout est calculé en JS depuis les sommets : les APIs ODA « toutes faites »
 * (`getDistAtParam`, `getArea`) ont des paramètres de sortie C++ inutilisables
 * dans le binding WASM. Les arcs des polylignes sont traités via leur bulge
 * (b = tan(θ/4)).
 *
 * Best-effort : `null` quand la mesure n'a pas de sens (texte, bloc, hachure)
 * ou que l'entité est illisible.
 */

import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import { deleteObj } from "../core/delete";

/** Orientation dominante d'un tracé linéaire, dans le repère du plan. */
export type EntityOrientation = "vertical" | "horizontal" | "oblique";

export interface EntityMeasure {
  /** Longueur développée (périmètre si fermé), en unités du plan. */
  length: number | null;
  /** Surface (contour fermé uniquement), en unités² du plan. */
  area: number | null;
  /**
   * Orientation dominante — tracés linéaires OUVERTS uniquement (fissure,
   * joint…). `null` pour les contours fermés (une zone n'a pas d'orientation
   * de désordre) et les courbes pures (arc, cercle, ellipse).
   */
  orientation: EntityOrientation | null;
}

const NO_MEASURE: EntityMeasure = {
  length: null,
  area: null,
  orientation: null,
};

/** Demi-ouverture (°) autour des axes X/Y pour classer horizontal/vertical. */
const AXIS_TOLERANCE_DEG = 25;

/**
 * Orientation dominante d'un nuage de sommets : axe principal (PCA 2D).
 * Plus robuste que le vecteur premier→dernier sur un tracé main levée sinueux.
 */
function dominantOrientation(verts: Vertex[]): EntityOrientation | null {
  const n = verts.length;
  let mx = 0;
  let my = 0;
  for (const v of verts) {
    mx += v.x;
    my += v.y;
  }
  mx /= n;
  my /= n;

  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (const v of verts) {
    const dx = v.x - mx;
    const dy = v.y - my;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }
  if (sxx + syy === 0) return null; // sommets confondus

  // Angle de l'axe principal, ramené dans [0, 180°).
  let deg = ((0.5 * Math.atan2(2 * sxy, sxx - syy)) * 180) / Math.PI;
  if (deg < 0) deg += 180;

  if (deg < AXIS_TOLERANCE_DEG || deg > 180 - AXIS_TOLERANCE_DEG) {
    return "horizontal";
  }
  if (Math.abs(deg - 90) <= AXIS_TOLERANCE_DEG) return "vertical";
  return "oblique";
}

interface Vertex {
  x: number;
  y: number;
  /** bulge du segment PARTANT de ce sommet (0 = droit). */
  bulge: number;
}

/** Longueur + surface algébrique d'un polygone à segments droits/arcs. */
function measureFromVertices(verts: Vertex[], closed: boolean): EntityMeasure {
  const n = verts.length;
  if (n < 2) return NO_MEASURE;

  const segCount = closed ? n : n - 1;
  let length = 0;
  // Shoelace signé + corrections des segments circulaires (signe du bulge).
  let area2 = 0; // 2 × aire du polygone à cordes
  let arcArea = 0;

  for (let i = 0; i < segCount; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const chord = Math.hypot(dx, dy);

    if (a.bulge !== 0 && chord > 0) {
      const theta = 4 * Math.atan(a.bulge); // angle balayé (signé)
      const radius = chord / (2 * Math.sin(Math.abs(theta) / 2));
      length += radius * Math.abs(theta);
      // Aire du segment circulaire (entre corde et arc), signée comme le bulge.
      arcArea += ((radius * radius) / 2) * (theta - Math.sin(theta));
    } else {
      length += chord;
    }

    area2 += a.x * b.y - b.x * a.y;
  }

  const area = closed ? Math.abs(area2 / 2 + arcArea) : null;
  const orientation = closed ? null : dominantOrientation(verts);
  return { length, area, orientation };
}

function measurePolyline(
  polyline: DrawingModule.OdDbPolyline,
  module: DrawingModuleType,
): EntityMeasure {
  const numVerts = polyline.numVerts();
  if (numVerts < 2) return NO_MEASURE;

  const verts: Vertex[] = [];
  const pt = new module.OdGePoint2d(0, 0);
  for (let i = 0; i < numVerts; i++) {
    polyline.getPointAt(i, pt); // rempli par référence
    verts.push({ x: pt.x, y: pt.y, bulge: polyline.getBulgeAt(i) });
  }
  deleteObj(pt);

  return measureFromVertices(verts, polyline.isClosed());
}

function measureLine(line: DrawingModule.OdDbLine): EntityMeasure {
  const start = line.startPoint();
  const end = line.endPoint();
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  const orientation = dominantOrientation([
    { x: start.x, y: start.y, bulge: 0 },
    { x: end.x, y: end.y, bulge: 0 },
  ]);
  deleteObj(start);
  deleteObj(end);
  return { length, area: null, orientation };
}

function measureCircle(circle: DrawingModule.OdDbCircle): EntityMeasure {
  const r = circle.radius();
  return { length: 2 * Math.PI * r, area: Math.PI * r * r, orientation: null };
}

function measureArc(arc: DrawingModule.OdDbArc): EntityMeasure {
  const r = arc.radius();
  let sweep = arc.endAngle() - arc.startAngle();
  if (sweep <= 0) sweep += 2 * Math.PI;
  return { length: r * sweep, area: null, orientation: null };
}

function measureEllipse(ellipse: DrawingModule.OdDbEllipse): EntityMeasure {
  // Seules les ellipses PLEINES sont mesurées (l'outil ellipse trace 0→2π) ;
  // un arc d'ellipse n'a ni aire ni longueur simple.
  const sweep = Math.abs(ellipse.endAngle() - ellipse.startAngle());
  if (Math.abs(sweep - 2 * Math.PI) > 1e-6) return NO_MEASURE;

  const a = ellipse.majorRadius();
  const b = ellipse.minorRadius();
  if (a + b === 0) return NO_MEASURE;

  // Périmètre (Ramanujan) — calculé par cohérence avec le cercle, mais filtré
  // en aval : un contour fermé n'expose que sa surface au contexte IA.
  const h = ((a - b) * (a - b)) / ((a + b) * (a + b));
  const length =
    Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
  return { length, area: Math.PI * a * b, orientation: null };
}

/**
 * Mesure une entité déjà ouverte en lecture. `null`/`null` pour les natures
 * non mesurables (texte, bloc, hachure, mleader…).
 */
export function measureEntity(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
): EntityMeasure {
  try {
    const polyline = module.OdDbPolyline.cast(entity);
    if (polyline) {
      const measure = measurePolyline(polyline, module);
      deleteObj(polyline);
      return measure;
    }

    const line = module.OdDbLine.cast(entity);
    if (line) {
      const measure = measureLine(line);
      deleteObj(line);
      return measure;
    }

    const circle = module.OdDbCircle.cast(entity);
    if (circle) {
      const measure = measureCircle(circle);
      deleteObj(circle);
      return measure;
    }

    const arc = module.OdDbArc.cast(entity);
    if (arc) {
      const measure = measureArc(arc);
      deleteObj(arc);
      return measure;
    }

    const ellipse = module.OdDbEllipse.cast(entity);
    if (ellipse) {
      const measure = measureEllipse(ellipse);
      deleteObj(ellipse);
      return measure;
    }
  } catch {
    // entité illisible — pas de mesure
  }
  return NO_MEASURE;
}
